import {
  parsePhoneNumber,
  formatPhoneNumber,
  formatPhoneNumberIntl,
} from "react-phone-number-input";
import { template } from "underscore";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";

import Constants from "./Constants";
import FieldUtils from "./FieldUtils";
import { Field } from "../types/TenantInterfaces";
import { FieldOptionsType } from "../components/Records/ComplexField/ComplexField";

dayjs.extend(localizedFormat);

export default class StrUtils {
  // Remove all non alpha numeric characters
  static cleanAPIName(apiName: string) {
    if (typeof apiName !== "string") return "";

    let updatedAPIName = apiName.replace(/[^0-9a-zA-Z]/gi, "");

    if (Constants.COSMOS_KEYWORDS.includes(updatedAPIName.toLowerCase())) {
      return "";
    }

    return updatedAPIName;
  }

  /**
   * Removes all duplicated values from an array
   */
  static unique(array: string[]) {
    if (!Array.isArray(array)) return [];

    return Array.from(new Set(array));
  }

  /**
   * Formats a number
   *
   * @param {*} number
   * @returns
   */
  static numberFormat(number: number) {
    let out = new Intl.NumberFormat("en-US");

    return out.format(number);
  }

  static shadeColor(color: string, percent: number) {
    let c = color;

    if (!color) {
      c = Constants.DEFAULT_COLOR;
    }

    let R = parseInt(c.substring(1, 3), 16);
    let G = parseInt(c.substring(3, 5), 16);
    let B = parseInt(c.substring(5, 7), 16);

    R = (R * (100 + percent)) / 100;
    G = (G * (100 + percent)) / 100;
    B = (B * (100 + percent)) / 100;

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    let RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
    let GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
    let BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

    return "#" + RR + GG + BB;
  }

  static formatNumber(num: number) {
    if (!num) {
      return "0";
    }

    return num?.toLocaleString("en-US");
  }

  static makeid(length: number) {
    let result = [];
    let characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result.push(
        characters.charAt(Math.floor(Math.random() * charactersLength))
      );
    }
    return result.join("");
  }

  static uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  static formatPhone(phone: string) {
    if (!phone || typeof phone !== "string") {
      return "";
    }

    const parsed = parsePhoneNumber(phone || "");

    if (!parsed) {
      return "";
    }

    if (parsed.countryCallingCode === "1") {
      return formatPhoneNumber(phone);
    } else {
      return formatPhoneNumberIntl(phone);
    }
  }

  static readableFromISOString(val = "") {
    if (!val) {
      return "";
    }

    let date = dayjs(val);

    return date.format("lll");
  }

  static templateConverter(templateString: string, variableValues: any) {
    if (typeof templateString !== "string" || !templateString) {
      return templateString;
    }

    try {
      let valTemplate = template(templateString, {
        interpolate: /\{\{(.+?)\}\}/g,
      });

      const val = valTemplate(variableValues);

      return val;
    } catch {
      return templateString;
    }
  }

  static templateToReadable(template: any, params: any) {
    try {
      template = FieldUtils.removeUndefinedVariablesFromTemplate(
        template,
        params
      );

      let temp = template(template || "", { interpolate: /\{\{(.+?)\}\}/g });

      // Parse out all of the regex variables and make sure they have defaults
      let found = [], // an array to collect the strings that are found
        rxp = /{{([^}]+)}}/g,
        curMatch;

      while ((curMatch = rxp.exec(template))) {
        found.push(curMatch[1]);
      }

      for (let i = 0; i < found.length; i++) {
        if (!params[found[i]]) {
          params[found[i]] = "";
        }
      }
      const aux = temp(params);
      return aux;
    } catch (e) {
      console.error(e);

      return "<template_error>";
    }
  }

  static getInitials(name: string) {
    const firstName = this.getFirstName(name);
    const lastName = this.getLastName(name);

    let out = "";

    if (firstName) {
      out += firstName[0];
    }

    if (lastName) {
      out += lastName[0];
    }

    return out;
  }

  static getFirstName(name: string) {
    if (!name) {
      return "";
    }

    const outArr = name.split(" ");

    return outArr[0];
  }

  static getLastName(name = "") {
    if (!name || typeof name !== "string") {
      return "";
    }

    let nameArr = name.split(" ");

    if (nameArr.length == 0) {
      return nameArr[0];
    }

    nameArr = nameArr.slice(1);

    return nameArr.join(" ");
  }

  static getFilterMethodsByFieldType(fieldType: string) {
    // Boolean type
    if (fieldType === Constants.FIELDTYPES.TOGGLE) {
      return [
        Constants.FILTERTYPES.EQUALS,
        Constants.FILTERTYPES.NOT_EQUALS,
        Constants.FILTERTYPES.NOT_EMPTY,
        Constants.FILTERTYPES.EMPTY,
      ];
    }

    // Text types (strings)
    if (
      fieldType === Constants.FIELDTYPES.TEXT ||
      fieldType === Constants.FIELDTYPES.LONG_TEXT ||
      fieldType === Constants.FIELDTYPES.URL ||
      fieldType === Constants.FIELDTYPES.EMAIL ||
      fieldType === Constants.FIELDTYPES.PHONE
    ) {
      return [
        Constants.FILTERTYPES.EQUALS,
        Constants.FILTERTYPES.NOT_EQUALS,
        Constants.FILTERTYPES.CONTAINS,
        Constants.FILTERTYPES.NOT_EMPTY,
        Constants.FILTERTYPES.EMPTY,
      ];
    }

    // Date/Numeric types
    if (
      fieldType === Constants.FIELDTYPES.NUMBER ||
      fieldType === Constants.FIELDTYPES.PERCENT ||
      fieldType === Constants.FIELDTYPES.DATE ||
      fieldType === Constants.FIELDTYPES.DATE_TIME ||
      fieldType === Constants.FIELDTYPES.TIME ||
      fieldType === Constants.FIELDTYPES.CURRENCY ||
      fieldType === Constants.FIELDTYPES.CURRENCY_INTERVAL
    ) {
      return [
        Constants.FILTERTYPES.EQUALS,
        Constants.FILTERTYPES.NOT_EQUALS,
        Constants.FILTERTYPES.GREATER_THAN,
        Constants.FILTERTYPES.GREATER_THAN_EQUALS,
        Constants.FILTERTYPES.LESS_THAN,
        Constants.FILTERTYPES.LESS_THAN_EQUALS,
        Constants.FILTERTYPES.BETWEEN,
        Constants.FILTERTYPES.NOT_BETWEEN,
        Constants.FILTERTYPES.NOT_EMPTY,
        Constants.FILTERTYPES.EMPTY,
      ];
    }

    // Single selectors
    if (
      fieldType === Constants.FIELDTYPES.SINGLE_SELECT ||
      fieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
    ) {
      return [
        Constants.FILTERTYPES.EQUALS,
        Constants.FILTERTYPES.NOT_EQUALS,
        Constants.FILTERTYPES.NOT_EMPTY,
        Constants.FILTERTYPES.EMPTY,
      ];
    }

    // Multi-Selectors
    if (
      fieldType === Constants.FIELDTYPES.MULTI_SELECT ||
      fieldType === Constants.FIELDTYPES.MULTI_REFERENCE ||
      fieldType === Constants.FIELDTYPES.RICH_TEXT ||
      fieldType === Constants.FIELDTYPES.OBJECT ||
      fieldType === "Tags"
    ) {
      return [
        Constants.FILTERTYPES.CONTAINS,
        Constants.FILTERTYPES.NOT_EMPTY,
        Constants.FILTERTYPES.EMPTY,
      ];
    }

    if (fieldType === Constants.FIELDTYPES.LOCATION) {
      return [
        Constants.FILTERTYPES.EQUALS,
        Constants.FILTERTYPES.NOT_EQUALS,
        Constants.FILTERTYPES.CONTAINS,
        Constants.FILTERTYPES.NOT_EMPTY,
        Constants.FILTERTYPES.EMPTY,
      ];
    }

    return [];
  }

  static convertMarkupToPlaintext(markup: string) {
    return markup
      .replace(/&[^\s;]+;|/g, "") // Remove HTML entities - Ex: &nbsp;
      .replace(/<[^>]+>/g, " ") // Remove HTML tags
      .replace(/\s\s+/g, " ") // Remove consecutive whitespace
      .trim();
  }

  static createNewSubModule() {
    const layoutID = this.uuidv4();
    const defaultFields = Constants.DEFAULT_QUERY_FIELDS;

    return {
      Title: "",
      Description: "",
      ModuleAPIName: "",
      Query: {
        Fields: defaultFields,
        Filters: [],
        Pagination: {
          Count: 25,
          Page: 1,
        },
        _SelectedLayout: layoutID,
      },
      ShowAddButton: false,
      SubModuleID: this.uuidv4(),
      Prefills: [],
      SavedFilters: [],
      TableLayouts: [
        {
          Name: "Main Layout",
          Fields: defaultFields,
          id: layoutID,
        },
      ],
    };
  }

  static convertCamelCase(str: string) {
    const result = str.replace(/([A-Z])/g, " $1");
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);

    return finalResult.trim();
  }

  static iconForField(field: Field) {
    if (!field?.FieldType) {
      return "mdi mdi-database";
    }

    switch (field?.FieldType) {
      case "Text":
      case "LongText":
      case "RichText":
        return "mdi mdi-format-text";
      case "SingleSelect":
      case "MultiSelect":
        return "mdi mdi-format-list-checks";
      case "SingleReference":
      case "MultiReference":
        if (field?.SourcesList) {
          if (field?.SourcesList?.indexOf("Settings.Users") >= 0) {
            return "mdi mdi-account-multiple";
          } else if (field?.SourcesList?.indexOf("Global.Lists") >= 0) {
            return "mdi mdi-format-list-checks";
          } else {
            return "mdi mdi-vector-link";
          }
        } else {
          return "mdi mdi-vector-link";
        }
      case "Email":
        return "mdi mdi-email";
      case "Object":
        return "mdi mdi-code-json";
      case "Toggle":
        return "mdi mdi-toggle-switch";
      case "Phone":
        return "mdi mdi-phone";
      case "URL":
        return "mdi mdi-earth";
      case "Number":
        return "mdi mdi-numeric";
      case "Currency":
      case "CurrencyInterval":
        return "mdi mdi-cash-multiple";
      case "Date":
        return "mdi mdi-calendar";
      case "DateTime":
        return "mdi mdi-calendar-clock";
      case "Time":
        return "mdi mdi-clock";
      case "Location":
        return "mdi mdi-map-marker";
      case "Image":
        return "mdi mdi-image";
      case "Subitem":
        return "mdi mdi-file-tree";
      case "MultiSubitem":
        return "mdi mdi-file-tree";
      default:
        return "mdi mdi-database-outline";
    }
  }

  static validateEmail(email: string) {
    const emailRegex = // eslint-disable-next-line
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(String(email).toLowerCase());
  }

  static validateURL(url: string) {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR IP (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$", // fragment locator
      "i"
    );
    return pattern.test(url);
  }

  static validateTextByType(text: string, type: string) {
    switch (type) {
      case "Email": {
        return StrUtils.validateEmail(text);
      }
      case "URL": {
        return StrUtils.validateURL(text);
      }
      case "Phone": {
        return text === null || text === undefined || text === ""
          ? false
          : true;
      }
      default:
        return true;
    }
  }

  static getComplexFieldOptions(fieldType: string) {
    if (fieldType === "Email") {
      return Constants.EMAIL_TYPE_OPTIONS;
    }
    if (fieldType === "URL") {
      return Constants.URL_TYPE_OPTIONS;
    }
    if (fieldType === "Phone") {
      return Constants.PHONE_TYPE_OPTIONS;
    }

    return [] as FieldOptionsType;
  }

  static getMentionedUserIds(richText: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(richText, "text/html");

    const mentionSpans = doc && doc?.querySelectorAll(".mention");

    if (!mentionSpans || !mentionSpans.length) return [];

    let ids = new Set<string>();

    // Extract data-id from each mention span (designed for Quill Mentions RichText)
    mentionSpans.forEach((span) => {
      const userId = span.getAttribute("data-id");

      if (userId) {
        ids.add(userId);
      }
    });

    return Array.from(ids);
  }
}
